import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Users, Award, Coffee, Clock, Code, Rocket, Heart, Star } from 'lucide-react';
import { PORTFOLIO_DATA } from '../../utils/constants';
import './About.css';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const achievements = [
    { icon: <Users size={24} />, number: '50+', label: 'Happy Clients', color: '#6366f1' },
    { icon: <Award size={24} />, number: '70+', label: 'Projects Done', color: '#8b5cf6' },
    { icon: <Coffee size={24} />, number: '1000+', label: 'Cups of Coffee', color: '#06b6d4' },
    { icon: <Clock size={24} />, number: '2+', label: 'Years Experience', color: '#10b981' }
  ];

  const skillCategories = [
    {
      title: 'Frontend',
      icon: <Code size={20} />,
      skills: ['React', 'Next.js', 'TypeScript', 'JavaScript', 'HTML5', 'CSS3'],
      color: '#61dafb'
    },
    {
      title: 'Backend',
      icon: <Rocket size={20} />,
      skills: ['Python', 'Django', 'Flask', 'Node.js', 'PostgreSQL', 'MongoDB'],
      color: '#3776ab'
    },
    {
      title: 'Tools & Others',
      icon: <Star size={20} />,
      skills: ['Git', 'Docker', 'AWS', 'Figma', 'Linux', 'Odoo'],
      color: '#f59e0b'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  const skillVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.68, -0.55, 0.265, 1.55]
      }
    }
  };

  return (
    <section id="about" className="about section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2 variants={itemVariants} className="section-title">
            About Me
          </motion.h2>
          <motion.p variants={itemVariants} className="section-subtitle">
            Get to know the person behind the code
          </motion.p>
        </motion.div>
        
        {/* Photo and Title Section */}
        <motion.div
          className="about-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="about-image">
            <div className="about-avatar-wrapper">
              <div className="about-avatar">
                <span>{PORTFOLIO_DATA.name[0]}</span>
                <div className="avatar-decoration">
                  <Heart className="decoration-icon heart" size={20} />
                  <Code className="decoration-icon code" size={18} />
                  <Rocket className="decoration-icon rocket" size={16} />
                </div>
              </div>
              <div className="about-decoration decoration-1"></div>
              <div className="about-decoration decoration-2"></div>
              <div className="about-decoration decoration-3"></div>
            </div>
          </motion.div>
          
          <div className="about-text">
            <motion.h3 variants={itemVariants} className="about-title">
              I'm <span className="gradient-text">{PORTFOLIO_DATA.name}</span>, a passionate {PORTFOLIO_DATA.role}
            </motion.h3>
            
            <motion.div variants={itemVariants} className="about-description">
              <p>
                {PORTFOLIO_DATA.description}
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Highlights Section - Side by Side */}
        <motion.div
          className="about-highlights"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="highlight-item">
            <div className="highlight-icon">
              <Code size={24} />
            </div>
            <div className="highlight-content">
              <h4>Clean Code Advocate</h4>
              <p>I believe in writing maintainable, scalable, and efficient code that stands the test of time.</p>
            </div>
          </motion.div>
          
          <motion.div variants={itemVariants} className="highlight-item">
            <div className="highlight-icon">
              <Rocket size={24} />
            </div>
            <div className="highlight-content">
              <h4>Innovation Driven</h4>
              <p>Always exploring new technologies and methodologies to deliver cutting-edge solutions.</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Skills Section - Full Width */}
        <motion.div
          className="about-skills"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h4 variants={itemVariants}>Skills & Technologies</motion.h4>
          <div className="skills-categories">
            {skillCategories.map((category, categoryIndex) => (
              <motion.div
                key={category.title}
                className="skill-category"
                variants={itemVariants}
              >
                <div className="category-header">
                  <div className="category-icon" style={{ color: category.color }}>
                    {category.icon}
                  </div>
                  <h5>{category.title}</h5>
                </div>
                <div className="category-skills">
                  {category.skills.map((skill, skillIndex) => (
                    <motion.span
                      key={skill}
                      className="skill-tag"
                      variants={skillVariants}
                      initial="hidden"
                      animate={inView ? "visible" : "hidden"}
                      transition={{
                        delay: categoryIndex * 0.1 + skillIndex * 0.05
                      }}
                    >
                      {skill}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        {/* Stats Section - Full Width */}
        <motion.div
          className="about-stats"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {achievements.map((item, index) => (
            <motion.div
              key={item.label}
              className="about-stat"
              variants={itemVariants}
              whileHover={{ 
                scale: 1.05,
                boxShadow: `0 10px 30px ${item.color}20`
              }}
              transition={{ duration: 0.2 }}
            >
              <div className="stat-icon" style={{ color: item.color }}>
                {item.icon}
              </div>
              <div className="stat-content">
                <motion.span
                  className="stat-number"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ 
                    delay: index * 0.1 + 0.5, 
                    duration: 0.6, 
                    ease: [0.68, -0.55, 0.265, 1.55]
                  }}
                >
                  {item.number}
                </motion.span>
                <p className="stat-label">{item.label}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="about-cta"
          variants={itemVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <a href="#contact" className="btn btn-primary">
            <Coffee size={20} />
            Let's Work Together
          </a>
          <a href="/resume.pdf" target="_blank" rel="noopener noreferrer" className="btn btn-outline">
            <Award size={20} />
            Download Resume
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default About;